/* import __COLOCATED_TEMPLATE__ from './setup-and-go-live-phone.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { ChannelType } from 'embercom/lib/workflows/fin-workflow-preview';
import type Store from '@ember-data/store';
import type RouterService from '@ember/routing/router-service';
import type finTrialService from 'embercom/services/fin-trial-service';
import { action } from '@ember/object';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';
import type IntlService from 'embercom/services/intl';
import type FinOptInService from 'embercom/services/fin-opt-in-service';
import { taskFor } from 'ember-concurrency-ts';
import type ContentWrapper from 'embercom/models/content-service/content-wrapper';
import type AiAgentSetupService from 'embercom/services/ai-agent-setup-service';
import { task } from 'ember-concurrency-decorators';
import { objectTypes } from 'embercom/models/data/matching-system/matching-constants';
import { type TaskGenerator } from 'ember-concurrency';
import { statisticKeys } from 'embercom/models/data/outbound/constants';
import {
  PHONE_CALL_CHANNEL,
  PHONE_CHANNEL,
} from 'embercom/controllers/apps/app/automation/fin-ai-agent/setup';

const SORT_BY = 'updated_at';
const SORT_DIRECTION_ASC = 'desc';

interface Arguments {
  selectedPhoneNumberId: string | undefined;
  hasContentReadyForFin: boolean;
  reloadSetupRuleset: (setupType: ChannelType) => Promise<void>;
  phoneSetupRuleset: $TSFixMe;
  openNewLineModal?: () => void;
  setSelectedLine?: (phoneNumber?: string) => void;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Arguments;
}

export default class SetupAndGoLivePhone extends Component<Signature> {
  @service declare appService: AppService;
  @service declare store: Store;
  @service declare contentEditorService: $TSFixMe;
  @service declare router: RouterService;
  @service declare finTrialService: finTrialService;
  @service declare finVoiceConfigService: FinVoiceConfigService;
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare finOptInService: FinOptInService;
  @service permissionsService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;
  @tracked openSectionId = '';
  @service declare aiAgentSetupService: AiAgentSetupService;
  @service declare outboundHomeService: any;

  @tracked ruleset: $TSFixMe;
  @tracked workflowsWithFinVoice: ContentWrapper[] = [];
  @tracked finWorkflowsLastPageHit = false;

  // API Token state
  @tracked apiToken: string | null = null;
  @tracked isLoadingToken = false;
  @tracked isCreatingToken = false;

  phoneNumberConfigurationSectionId = 'phone-number-configuration';
  workflowConfigurationSectionId = 'workflow-configuration';

  workflowsOverview = {
    currentPage: 0,
    objectTypes: [
      objectTypes.customBot,
      objectTypes.inboundCustomBot,
      objectTypes.buttonCustomBot,
      objectTypes.triggerableCustomBot,
    ],
  };

  get selectedPhoneNumberId() {
    return this.args.selectedPhoneNumberId;
  }

  get selectedNumberConfiguration() {
    if (!this.selectedPhoneNumberId) {
      return undefined;
    }

    return this.finVoiceConfigService.numberConfigurations[this.selectedPhoneNumberId];
  }

  get canViewPhoneNumberPurchase() {
    return this.appService.app.canUseFeature('team-fin-voice-phone-number-purchase');
  }

  get numberConfigurations() {
    return this.finVoiceConfigService.numberConfigurations;
  }

  get hasNumberConfigurations() {
    return Object.keys(this.numberConfigurations).length > 0;
  }

  get phoneNumbers() {
    return this.finVoiceConfigService.phoneNumbers;
  }

  get parentAppUrl(): string {
    return this.router.urlFor(
      'apps.app.automation.fin-ai-agent.setup',
      this.appService.app.parent_app_id,
    );
  }

  get isLoadingWorkflows(): boolean {
    return taskFor(this.fetchFinWorkflows).isRunning;
  }

  get pageSize() {
    return this.aiAgentSetupService.shouldShowOnlyOneDeployMethod ? 50 : 3;
  }

  get isFinLiveInWorkflows() {
    return this.aiAgentSetupService.finStats.workflowsCount.liveFinPhoneWorkflowsCount > 0;
  }

  get liveFinPhoneWorkflowsCount() {
    return this.aiAgentSetupService.finStats.workflowsCount.liveFinPhoneWorkflowsCount;
  }

  get phoneChannelType() {
    return PHONE_CHANNEL;
  }

  get hasFinWorkflowsForPhoneChannel(): boolean {
    return this.workflowsWithFinVoice.length > 0;
  }

  constructor(owner: unknown, args: any) {
    super(owner, args);

    if (this.appService.app.finBlockedInTestApp) {
      return;
    }

    let currentRuleset = this.contentEditorService.ruleset;
    this.contentEditorService.unregister(currentRuleset);

    this.updateRuleset(this.args.phoneSetupRuleset, true);

    taskFor(this.fetchFinWorkflows).perform();
  }

  async contentSearch(pageFrom: number): Promise<object> {
    return this.outboundHomeService.contentSearchWithFilters(
      this.getWorkflowsSearchParams(pageFrom),
    );
  }

  private getWorkflowsSearchParams(pageFrom: number) {
    return {
      object_types: this.workflowsOverview.objectTypes,
      app_id: this.appService.app.id,
      per_page: this.pageSize,
      page_from: pageFrom,
      statistics: [statisticKeys.receipts],
      additionalSearchableData: {
        has_fin: true,
        target_channels: PHONE_CALL_CHANNEL,
      },
      sort_by: SORT_BY,
      sort_direction: SORT_DIRECTION_ASC,
      intersect_keys: ['additional_searchable_data.target_channels'],
    };
  }

  @task({ restartable: true })
  *fetchFinWorkflows(): TaskGenerator<void> {
    let response = yield this.contentSearch(this.workflowsOverview.currentPage);
    this.workflowsOverview.currentPage = 0;
    this.workflowsWithFinVoice = response.contentWrappers;
    // we only need to show 3 workflows in deploy page,
    // so we set finWorkflowsLastPageHit to true to let the pagination component not show the load more button
    this.finWorkflowsLastPageHit = true;
  }

  @task({ drop: true })
  *loadMoreFinWorkflows(): TaskGenerator<void> {
    this.workflowsOverview.currentPage++;
    let response = yield this.contentSearch(this.workflowsOverview.currentPage);
    this.workflowsWithFinVoice = [...this.workflowsWithFinVoice, ...response.contentWrappers];
    // we only need to show 3 workflows in deploy page,
    // so we set finWorkflowsLastPageHit to true to let the pagination component not show the load more button
    this.finWorkflowsLastPageHit = true;
  }

  willDestroy(): void {
    super.willDestroy();
  }

  @action
  goToVoiceTesting() {
    if (this.appService.app.isStandaloneApp) {
      this.router.transitionTo('apps.app.phone.standalone.deploy.test');
    } else {
      this.router.transitionTo({ queryParams: { tab: 'voice-playground' } });
    }
  }

  @action
  async updatePercentageRollout(
    phoneNumberId: string,
    rollOutPercentageIn: number | undefined,
    rollOutPercentageNin: number | undefined,
  ) {
    try {
      let ruleset = this.ruleset;

      if (!ruleset) {
        return;
      }

      // to get here, customer has seen the Fin AI Agent overview landing page with all the terms for using Fin
      if (ruleset.isDraft && !this.finOptInService.hasOptedInToFin) {
        await taskFor(this.finOptInService.postAcceptOptIn).perform();
      }

      await this.finVoiceConfigService.updatePercentageRollout(
        phoneNumberId,
        rollOutPercentageIn,
        rollOutPercentageNin,
      );

      await this.updateRuleset(ruleset);

      if (ruleset.isLive) {
        await this.finTrialService.openFinRetrialConfirmationModal();
      }

      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.success'),
      );
    } catch (error) {
      console.error('error', error);
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.failure'),
      });
    }
  }

  @action
  async updateRuleset(ruleset: $TSFixMe, register = false) {
    this.ruleset = await this.store.findRecord('matching-system/ruleset', ruleset.id);
    await this.args.reloadSetupRuleset('phone');

    if (register && this.contentEditorService.ruleset?.id !== this.ruleset.id) {
      this.contentEditorService.register({ ruleset: this.ruleset });
    }
  }

  @action
  setSelectedLine(phoneNumberId: string) {
    this.args.setSelectedLine?.(phoneNumberId);
  }

  @action
  resetSelectedLine() {
    this.args.setSelectedLine?.(undefined);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::SetupAndGoLivePhone': typeof SetupAndGoLivePhone;
  }
}
